import { axiosBaseQuery, createAxiosInstance } from '@client';
import { getEnvironmentVariables } from '@common';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { HlAiBankAccountDetails } from '@state';
import { S3ObjectResponse } from '@module';
import { HlAiDocumentRecogniserResponse } from '@state/types/HlAiDocumentRecogniserResponse';
import { PdaFdaAttachmentValidationPayload } from './models';
import {
  HlAiNegotiatorBenchmarkResposne,
  HlAiNegotiatorEmailResposne,
  HlAiNegotiatorPdaDocumentResponse,
  HlAiNegotiatorRangesDocumentResponse,
  HlAiParameterResponse
} from '@state/types/HlAiNegotiatorResponse';
import { HlAiHusbandrySummaryResponse } from '@state/types/HlAiHusbandrySummaryResponse';

const { HL_AI_API_URL } = getEnvironmentVariables();

const axiosHarborlabAiInstance = createAxiosInstance({
  baseURLParam: `${HL_AI_API_URL}`
});

export type ExtractBankAccountDetailsTrigger = ReturnType<
  typeof useExtractBankAccountDetailsMutation
>[0];

export type ExtractDocumentDetailsSyncTrigger = ReturnType<
  typeof useExtractDocumentTextSyncMutation
>[0];

export type PollForResultsMutationTrigger = ReturnType<
  typeof usePollForResultsMutation
>[0];

export type VerifyDocumentTypeTrigger = ReturnType<
  typeof useVerifyDocumentTypeMutation
>[0];

export type PollForDocumentTypeVerificationTrigger = ReturnType<
  typeof usePollForDocumentTypeVerificationMutation
>[0];

export type AiParametersInputsTrigger = ReturnType<
  typeof useAiParametersInputsMutation
>[0];

export type AiParametersEmailAgentTrigger = ReturnType<
  typeof useAiParametersEmailAgentMutation
>[0];

export type PdaFdaValidationOcrTrigger = ReturnType<
  typeof usePdaFdaValidationOcrMutation
>[0];

export type PollForPdaFdaOcrResultsTrigger = ReturnType<
  typeof usePollForPdaFdaOcrResultsMutation
>[0];

export type PdaFdaValidatorTrigger = ReturnType<
  typeof usePdaFdaValidatorMutation
>[0];

export type PollForPdaFdaValidationResultsTrigger = ReturnType<
  typeof usePollForPdaFdaValidationResultsMutation
>[0];

export type HusbandrySummaryResponseTrigger = ReturnType<
  typeof useGetHusbandrySummaryMutation
>[0];

export const harborlabAiApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: '',
    customAxiosInstance: axiosHarborlabAiInstance
  }),
  reducerPath: 'harborlab-ai',
  endpoints: (build) => ({
    aiParametersInputs: build.mutation<
      void,
      {
        proforma_id: number;
        uuid: string;
      }
    >({
      query: ({ proforma_id, uuid }) => ({
        url: '/ai-parameters-inputs',
        method: 'POST',
        data: {
          da_id: proforma_id,
          uuid
        }
      })
    }),
    aiParametersEmailAgent: build.mutation<
      void,
      {
        proforma_id: number;
        uuid: string;
      }
    >({
      query: ({ proforma_id, uuid }) => ({
        url: '/ai-parameters-email-agent',
        method: 'POST',
        data: {
          da_id: proforma_id,
          uuid
        }
      })
    }),
    extractBankAccountDetails: build.mutation<
      HlAiBankAccountDetails,
      S3ObjectResponse
    >({
      query: (payload) => ({
        url: '/bank-account-details',
        method: 'POST',
        data: payload
      })
    }),
    pollForResults: build.mutation<
      | HlAiBankAccountDetails
      | HlAiNegotiatorPdaDocumentResponse
      | HlAiNegotiatorRangesDocumentResponse
      | HlAiNegotiatorBenchmarkResposne
      | HlAiNegotiatorEmailResposne
      | HlAiParameterResponse
      | HlAiHusbandrySummaryResponse,
      { s3ObjectDetails: S3ObjectResponse; fileName: string }
    >({
      query: ({ s3ObjectDetails, fileName }) => ({
        url: '/results-poller',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          fileName
        }
      })
    }),
    verifyDocumentType: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse; documentType: string; uuid: string }
    >({
      query: ({ s3ObjectDetails, documentType, uuid }) => ({
        url: '/document-recogniser',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          documentType,
          uuid
        }
      })
    }),
    pollForDocumentTypeVerification: build.mutation<
      HlAiDocumentRecogniserResponse,
      { s3ObjectDetails: S3ObjectResponse; uuid: string }
    >({
      query: ({ s3ObjectDetails, uuid }) => ({
        url: '/document-recogniser-results-poller',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          uuid
        }
      })
    }),
    extractDocumentText: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse; companyName?: string }
    >({
      query: ({ s3ObjectDetails, companyName }) => ({
        url: '/pdf-text-extractor',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3Key: s3ObjectDetails.s3FileKey,
          companyName
        }
      })
    }),
    extractDocumentTextSync: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse }
    >({
      query: ({ s3ObjectDetails }) => ({
        url: '/pdf-text-extractor-sync',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3Key: s3ObjectDetails.s3FileKey
        }
      })
    }),
    extractSofEvents: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse; uuid: string }
    >({
      query: ({ s3ObjectDetails, uuid }) => ({
        url: '/sof-event-extractor',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          uuid
        }
      })
    }),
    // API Gateway invokes this Lambda asynchronously (isAsync) and
    // returns 202. The OCR artifact is read from S3 via the poller.
    pdaFdaValidationOcr: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse }
    >({
      query: ({ s3ObjectDetails }) => ({
        url: '/pda-fda-validation-ocr',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3Key: s3ObjectDetails.s3FileKey
        }
      })
    }),
    pollForPdaFdaOcrResults: build.mutation<
      unknown,
      { s3ObjectDetails: S3ObjectResponse }
    >({
      query: ({ s3ObjectDetails }) => ({
        url: '/pda-fda-ocr-results-poller',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          pdfS3Key: s3ObjectDetails.s3FileKey
        }
      })
    }),
    pdaFdaValidator: build.mutation<
      void,
      {
        s3Bucket: string;
        ocrArtifactKey: string;
        validationPayload: PdaFdaAttachmentValidationPayload;
      }
    >({
      query: ({ s3Bucket, ocrArtifactKey, validationPayload }) => ({
        url: '/pda-fda-validator',
        method: 'POST',
        data: {
          s3Bucket,
          ocrArtifactKey,
          payload: validationPayload
        }
      })
    }),
    pollForPdaFdaValidationResults: build.mutation<
      unknown,
      {
        s3Bucket: string;
        ocrArtifactKey: string;
      }
    >({
      query: ({ s3Bucket, ocrArtifactKey }) => ({
        url: '/pda-fda-validation-results-poller',
        method: 'POST',
        data: {
          s3Bucket,
          ocrArtifactKey
        }
      })
    }),
    getHusbandrySummary: build.mutation<
      void,
      {
        s3Bucket?: string;
        s3FileKeys: string[];
        uuid: string;
      }
    >({
      query: ({ s3Bucket, s3FileKeys, uuid }) => ({
        url: '/husbandry-event-summarizer',
        method: 'POST',
        data: {
          s3Bucket,
          s3FileKeys,
          uuid
        }
      })
    })
  })
});

export type StateWithHarborlabAiApi = {
  [harborlabAiApi.reducerPath]: ReturnType<typeof harborlabAiApi.reducer>;
};

export const {
  useAiParametersInputsMutation,
  useAiParametersEmailAgentMutation,
  useExtractBankAccountDetailsMutation,
  useExtractDocumentTextMutation,
  useExtractDocumentTextSyncMutation,
  useExtractSofEventsMutation,
  usePollForDocumentTypeVerificationMutation,
  usePollForResultsMutation,
  useVerifyDocumentTypeMutation,
  usePdaFdaValidationOcrMutation,
  usePollForPdaFdaOcrResultsMutation,
  usePdaFdaValidatorMutation,
  usePollForPdaFdaValidationResultsMutation,
  useGetHusbandrySummaryMutation
} = harborlabAiApi;
